/**
 * 工作区报告常量定义
 *
 * @module clinical-record/infrastructure/stores/constants
 */

import type { TabName } from "./chatStore";

// ==================== 类型定义 ====================

/** 工作区报告状态 */
export type WorkspaceReportState = "待生成" | "生成中" | "已生成" | "生成失败";

/** 工作区文库分区 */
export type WorkspaceLibrarySection =
  | "official_record"
  | "ai_report"
  | "quality_report";

/** 工作区来源类型 */
export type WorkspaceSourceKind =
  | "current_medical_record"
  | "ai_generated"
  | "ai_promoted";

/** 工作区审核状态 */
export type WorkspaceReviewStatus =
  | "draft"
  | "reviewed"
  | "signed"
  | "returned";

// ==================== 常量定义 ====================

/** 任务键到报告键的映射 */
export const REPORT_KEY_BY_TASK: Record<string, string> = {
  "diagnosisTreatment:临床诊断": "initialDiagnosisReport",
  "diagnosisTreatment:治疗方案": "treatmentPlanReport",
  "diagnosisTreatment:病情分析": "clinicalAnalysisReport",
  "diagnosisTreatment:随访复诊": "followupPlanReport",
  "syndromeDifferentiation:汇通学派": "huitongSchoolReport",
  "syndromeDifferentiation:现代学派": "modernSchoolReport",
  "tcmHealthCare:中医膳食": "tcmDietReport",
  "tcmHealthCare:临床营养": "clinicalNutritionReport",
  "medicalWriting:入院记录": "admissionRecord",
  "medicalWriting:门诊病历": "outpatientRecord",
  "medicalWriting:首次病程记录": "firstCourseRecord",
  "medicalWriting:日常病程记录": "dailyCourseRecord",
  "medicalWriting:上级医师查房记录": "attendingRoundRecord",
  "medicalWriting:阶段小结": "stageSummaryRecord",
  "medicalWriting:手术记录": "surgeryRecord",
  "medicalWriting:麻醉记录": "anesthesiaRecord",
  "medicalWriting:疑难病例讨论记录": "difficultCaseDiscussion",
  "medicalWriting:转出记录": "transferOutRecord",
  "medicalWriting:转入记录": "transferInRecord",
  "medicalWriting:抢救记录": "rescueRecord",
  "medicalWriting:死亡病例讨论记录": "deathCaseDiscussion",
  "medicalWriting:死亡记录": "deathRecord",
  "medicalWriting:出院记录": "dischargeRecord",
  "medicalWriting:护理记录": "nursingRecord",
  "basicHealthCare:门诊病历检查": "recordQualityReport",
  "basicHealthCare:中医内容检查": "recordQualityReport",
  "basicHealthCare:中药处方检查": "recordQualityReport",
  "basicHealthCare:病历重点内容检查": "recordQualityReport",
  "publicHealthService:高血压病": "chronicManagementReport",
  "publicHealthService:高血压病慢病管理": "chronicManagementReport",
  "publicHealthService:糖尿病": "chronicManagementReport",
  "publicHealthService:糖尿病慢病管理": "chronicManagementReport",
  "publicHealthService:COPD": "chronicManagementReport",
  "publicHealthService:COPD慢病管理": "chronicManagementReport",
  "publicHealthService:冠心病": "chronicManagementReport",
  "publicHealthService:冠心病慢病管理": "chronicManagementReport",
  "publicHealthService:哮喘": "chronicManagementReport",
  "publicHealthService:哮喘慢病管理": "chronicManagementReport",
} as const;

/** 报告键到标题的映射 */
export const REPORT_TITLE_BY_KEY: Record<string, string> = {
  initialDiagnosisReport: "初步诊断报告",
  clinicalAnalysisReport: "病情分析报告",
  admissionRecord: "入院记录",
  constitutionReport: "体质调理报告",
  huitongSchoolReport: "汇通学派报告",
  modernSchoolReport: "现代学派报告",
  tcmDietReport: "中医膳食报告",
  clinicalNutritionReport: "临床营养报告",
  treatmentPlanReport: "诊疗方案报告",
  followupPlanReport: "随访复诊计划",
  recordQualityReport: "病历质控报告",
  chronicManagementReport: "慢病管理报告",
  officialMedicalRecordSnapshot: "病历快照",
  outpatientRecord: "门诊病历",
  firstCourseRecord: "首次病程记录",
  dailyCourseRecord: "日常病程记录",
  attendingRoundRecord: "上级医师查房记录",
  stageSummaryRecord: "阶段小结",
  surgeryRecord: "手术记录",
  anesthesiaRecord: "麻醉记录",
  difficultCaseDiscussion: "疑难病例讨论记录",
  transferOutRecord: "转出记录",
  transferInRecord: "转入记录",
  rescueRecord: "抢救记录",
  deathCaseDiscussion: "死亡病例讨论记录",
  deathRecord: "死亡记录",
  dischargeRecord: "出院记录",
  nursingRecord: "护理记录",
  officialOutpatientRecord: "门诊病历",
  officialFirstCourseRecord: "首次病程记录",
  officialDailyCourseRecord: "日常病程记录",
  officialDischargeRecord: "出院记录",
} as const;

/** 占位符姓名列表 */
export const PLACEHOLDER_NAMES = [
  "张XX",
  "李XX",
  "王XX",
  "赵XX",
  "陈XX",
  "刘XX",
] as const;

// ==================== 工具函数 ====================

/**
 * 生成任务键
 * @param tab 标签页名称
 * @param option 选项
 */
export function taskKey(tab: TabName, option: string): string {
  return `${tab}:${option}`;
}

/**
 * 回退选项到报告键
 * @param tab 标签页名称
 * @param option 选项
 */
export function fallbackOptionToReportKey(
  tab: TabName,
  option: string,
): string {
  const mappedKey = REPORT_KEY_BY_TASK[taskKey(tab, option)];
  if (mappedKey) return mappedKey;
  if (tab === "medicalWriting") return "admissionRecord";
  if (tab === "syndromeDifferentiation" || tab === "tcmHealthCare")
    return "constitutionReport";
  return "initialDiagnosisReport";
}

/**
 * 选项到文档标签
 * @param tab 标签页名称
 * @param option 选项
 * @param reportKey 报告键
 */
export function optionToDocumentLabel(
  tab: TabName,
  option: string,
  reportKey: string,
): string {
  if (tab === "medicalWriting" && option) return option;
  if (tab === "diagnosisTreatment" && option) {
    const labelByOption: Record<string, string> = {
      病情分析: "病情分析报告",
      临床诊断: "初步诊断报告",
      治疗方案: "诊疗方案报告",
      随访复诊: "随访复诊计划",
    };
    return (
      labelByOption[option] || REPORT_TITLE_BY_KEY[reportKey] || `${option}报告`
    );
  }
  if (tab === "syndromeDifferentiation" && option) return `${option}报告`;
  if (tab === "tcmHealthCare" && option) return `${option}报告`;
  return REPORT_TITLE_BY_KEY[reportKey] || option || "诊疗报告";
}

/**
 * 规范化文库分区
 */
export function normalizeLibrarySection(
  value?: string,
): WorkspaceLibrarySection {
  if (value === "official_record") return "official_record";
  if (value === "quality_report") return "quality_report";
  return "ai_report";
}

/**
 * 规范化来源类型
 */
export function normalizeSourceKind(value?: string): WorkspaceSourceKind {
  if (value === "current_medical_record" || value === "ai_promoted")
    return value;
  return "ai_generated";
}

/**
 * 规范化审核状态
 */
export function normalizeReviewStatus(value?: string): WorkspaceReviewStatus {
  if (value === "reviewed" || value === "signed" || value === "returned")
    return value;
  return "draft";
}

/**
 * 规范化报告状态
 */
export function normalizeState(state: string): WorkspaceReportState {
  return ["待生成", "生成中", "已生成", "生成失败"].includes(state)
    ? (state as WorkspaceReportState)
    : "已生成";
}

/**
 * 解析时间戳
 */
export function parseTime(value: string | null): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * 清理文件名部分
 */
export function sanitizeFileNamePart(value: string): string {
  return (value || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "")
    .trim();
}

/**
 * 选择占位符姓名
 */
export function pickPlaceholderName(seed = Date.now()): string {
  return PLACEHOLDER_NAMES[Math.abs(seed) % PLACEHOLDER_NAMES.length];
}
